// Semana 3 do Ciclo 1 da Bcorp: segmentos nas linhas, os 4 filtros nas colunas, nota de 1 a 3.
// ACEITE vem dos dados (só leitura). CAPACIDADE, POSICIONAMENTO e TIMING são dados por uma pessoa
// (nota + motivo); a IA da página "Onde a Hydrodata ganha" só deixa uma sugestão ("usar" copia para o campo).
// As notas ficam gravadas por segmento no servidor (uma linha por segmento, com revisão para não pisar em ninguém).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App as AntApp, Button, Input, Rate, Skeleton, Space, Switch, Table, Tag, Typography } from 'antd';
import { BulbOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { conteudoBeachhead, ehConflito, ehSemAcesso, erroDe, salvarLinhaBeachhead, type ConteudoBeachhead } from '../api';
import {
  aplicarSugestao, camposAlterados, doisPrimeiros, errosDaLinha, FILTROS, ordenarLinhas, ROTULO_FILTRO, somaDe, textoQuando,
  type CamposBeachhead, type FiltroPessoa, type LinhaBeachhead,
} from './beachheadRegras';
import type { PropsPagina } from './tipos';

const { Text } = Typography;

const COR_ACEITE: Record<number, string> = { 1: 'red', 2: 'gold', 3: 'green' };
const PEQUENO = { fontSize: 12 } as const;

export default function BeachheadFiltros({ tarefa, podeVer, podeEditar }: PropsPagina) {
  const { message } = AntApp.useApp();
  const [dados, setDados] = useState<ConteudoBeachhead | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [semAcesso, setSemAcesso] = useState(false);
  const [edicoes, setEdicoes] = useState<Record<string, CamposBeachhead>>({});
  const [salvando, setSalvando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await conteudoBeachhead(tarefa.id));
      setErro(null);
    } catch (e) {
      if (ehSemAcesso(e)) setSemAcesso(true); else setErro(erroDe(e));
    }
  }, [tarefa.id]);

  useEffect(() => { if (podeVer) void carregar(); }, [podeVer, carregar]);

  // A ordem e o resumo seguem o que está GRAVADO: a tabela só se reordena depois de salvar, não enquanto se digita.
  const ordenadas = useMemo(() => (dados ? ordenarLinhas(dados.linhas) : []), [dados]);
  const resumo = useMemo(() => {
    if (!dados) return null;
    return { total: dados.linhas.length, completas: dados.linhas.filter((l) => l.completa).length, primeiros: doisPrimeiros(dados.linhas) };
  }, [dados]);

  if (!podeVer || semAcesso) {
    return <Alert type="warning" showIcon title="Sem acesso" description="Esta tabela lê os negócios do comercial e é só para quem pode ver o painel do comercial." />;
  }
  if (erro) return <Alert type="error" showIcon title="Não foi possível carregar a tabela" description={erro} action={<Button size="small" onClick={() => void carregar()}>Tentar de novo</Button>} />;
  if (!dados || !resumo) return <Skeleton active />;

  const limite = dados.limite_motivo;
  const mudar = (chave: string, patch: CamposBeachhead) => setEdicoes((e) => ({ ...e, [chave]: { ...(e[chave] || {}), ...patch } }));
  const descartar = (chave: string) => setEdicoes((e) => { const n = { ...e }; delete n[chave]; return n; });
  const travado = (l: LinhaBeachhead) => !podeEditar || salvando === l.chave;

  const salvar = async (base: LinhaBeachhead) => {
    const campos = camposAlterados(base, edicoes[base.chave] || {});
    if (!Object.keys(campos).length) { descartar(base.chave); return; }
    const erros = errosDaLinha(campos, limite);
    if (erros.length) { message.warning(erros[0]); return; }
    setSalvando(base.chave);
    try {
      const nova = await salvarLinhaBeachhead(tarefa.id, base.chave, campos, { revisao: base.revisao });
      descartar(base.chave);
      message.success('Tabela gravada.');
      if (nova) {
        setDados((d) => (d ? { ...d, linhas: d.linhas.map((x) => (x.chave === base.chave ? nova : x)) } : d));
      } else {
        await carregar();
      }
    } catch (e) {
      if (ehConflito(e)) {
        message.warning('Outra pessoa mudou esta linha. Recarreguei a tabela; confira e preencha de novo.');
        descartar(base.chave);
        await carregar();
      } else if (ehSemAcesso(e)) {
        setSemAcesso(true);
      } else {
        message.error(erroDe(e));
      }
    } finally {
      setSalvando(null);
    }
  };

  // Uma coluna de filtro de pessoa: estrelas numeradas (clicar na mesma nota limpa), motivo e a sugestão da IA.
  const colunaFiltro = (f: FiltroPessoa) => ({
    title: ROTULO_FILTRO[f], width: 290,
    render: (_: unknown, l: LinhaBeachhead) => {
      const ed = edicoes[l.chave] || {};
      const kn = `${f}_nota` as const;
      const km = `${f}_motivo` as const;
      const nota = (kn in ed ? ed[kn] : l[f].nota) || null;
      const motivo = (km in ed ? ed[km] : l[f].motivo) || '';
      const sug = l.sugestao_ia?.[f];
      const celulaVazia = nota == null && !motivo.trim();
      return (
        <div style={{ display: 'grid', gap: 4 }}>
          <Rate
            count={3} allowClear value={nota ?? 0} disabled={travado(l)}
            character={({ index }) => <span>{(index ?? 0) + 1}</span>}
            onChange={(v) => mudar(l.chave, { [kn]: v || null } as CamposBeachhead)}
          />
          <Input
            size="small" maxLength={limite} value={motivo} disabled={travado(l)} placeholder="Por quê?"
            onChange={(ev) => mudar(l.chave, { [km]: ev.target.value } as CamposBeachhead)}
          />
          {sug && (sug.nota != null || sug.motivo) && celulaVazia && (
            <Text type="secondary" style={PEQUENO}>
              <BulbOutlined /> IA: {sug.nota ?? 'a decidir'}{sug.motivo ? ` — ${sug.motivo}` : ''}{' '}
              {podeEditar && (
                <Button
                  type="link" size="small" style={{ padding: 0 }} disabled={salvando === l.chave}
                  onClick={() => setEdicoes((e) => ({ ...e, [l.chave]: aplicarSugestao(e[l.chave] || {}, l, f) }))}
                >usar</Button>
              )}
            </Text>
          )}
        </div>
      );
    },
  });

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gap: 4 }}>
        <Text>
          Cada linha é um segmento de clientes. Cada coluna é um filtro da Bcorp, com nota de 1 a 3. A soma das quatro notas ordena a tabela;
          a ordem muda depois que você grava.
        </Text>
        <Space orientation="vertical" size={2}>
          {(['aceite', ...FILTROS] as const).map((f) => (
            <Text key={f} type="secondary" style={PEQUENO}>
              <b>{ROTULO_FILTRO[f]}:</b> {dados.definicoes[f] || '—'}{f === 'aceite' && dados.limiares_aceite ? ` Nota: ${dados.limiares_aceite}.` : ''}
            </Text>
          ))}
        </Space>
        {dados.ia && (
          <Text type="secondary" style={PEQUENO}>
            <BulbOutlined /> Sugestão da IA do texto de {textoQuando(dados.ia.concluido_em) || 'data desconhecida'} da página Onde a Hydrodata ganha.{' '}
            <Link to="/comercial?aba=onde-ganha">Abrir essa página</Link>
          </Text>
        )}
      </div>

      {!podeEditar && <Alert type="info" showIcon title="Você pode ver a tabela, mas não preencher." />}

      {!dados.linhas.length ? (
        <Alert type="info" showIcon title="Ainda não há segmento medido. A tabela lê os negócios decididos desde 2025." />
      ) : (
        <>
          <Space size={[24, 4]} wrap>
            <Text><b>{resumo.completas}</b> de {resumo.total} segmentos com as 4 notas</Text>
            {resumo.primeiros.length === 2
              ? <Text>Os dois primeiros: <b>{resumo.primeiros[0]}</b> e <b>{resumo.primeiros[1]}</b></Text>
              : <Text type="secondary">Faltam notas para apontar os dois primeiros.</Text>}
          </Space>

          <Table<LinhaBeachhead>
            size="small" pagination={false} rowKey="chave" dataSource={ordenadas} scroll={{ x: 'max-content' }}
            onRow={(l) => ({ style: (edicoes[l.chave]?.candidato ?? l.candidato) ? undefined : { opacity: 0.6 } })}
            columns={[
              {
                title: 'Segmento', width: 190, fixed: 'left',
                render: (_, l) => (
                  // os números do segmento (negócios e taxa) já estão no motivo do aceite: cada número uma vez
                  <Text strong>{l.segmento}</Text>
                ),
              },
              {
                title: 'Aceite (dados)', width: 190,
                render: (_, l) => (
                  <div>
                    <Tag color={COR_ACEITE[l.aceite.nota] || 'default'}>{l.aceite.nota}</Tag>
                    <Text type="secondary" style={PEQUENO}>{l.aceite.motivo}</Text>
                  </div>
                ),
              },
              ...FILTROS.map(colunaFiltro),
              {
                title: 'Soma', width: 120,
                render: (_, l) => {
                  const s = somaDe(l, edicoes[l.chave]);
                  return s.soma != null ? <Text strong>{s.soma}</Text> : <Text type="secondary">— (parcial {s.parcial})</Text>;
                },
              },
              {
                title: 'Na mesa', width: 90,
                render: (_, l) => (
                  <Switch
                    checked={edicoes[l.chave]?.candidato ?? l.candidato} disabled={travado(l)}
                    checkedChildren="Sim" unCheckedChildren="Não"
                    onChange={(v) => mudar(l.chave, { candidato: v })}
                  />
                ),
              },
              {
                title: '', width: 170, fixed: 'right',
                render: (_, l) => {
                  const mexeu = Object.keys(camposAlterados(l, edicoes[l.chave] || {})).length > 0;
                  if (podeEditar && mexeu) {
                    return (
                      <Space size={4}>
                        <Button size="small" type="primary" loading={salvando === l.chave} onClick={() => void salvar(l)}>Salvar</Button>
                        <Button size="small" disabled={salvando === l.chave} onClick={() => descartar(l.chave)}>Descartar</Button>
                      </Space>
                    );
                  }
                  return !mexeu && l.completa ? <Tag color="green">Completa</Tag> : null;
                },
              },
            ]}
          />
        </>
      )}
    </div>
  );
}
